'use client'

import { Fragment } from 'react'
import Link from 'next/link'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { ChevronRight, Heart, LogOut, MapPin, Medal, PackageSearch, Settings, ShieldCheck, X, type LucideIcon } from 'lucide-react'
import Avatar from 'boring-avatars'
import { formatImageURL, isLoyaltyActive } from '@/utils/util'
import { useProfileNav, type ProfileNavKey } from '@/lib/commerce/hooks'
import Skeleton from '@/components/elements/Skeleton'

const ICONS: Record<ProfileNavKey, LucideIcon> = {
  orders: PackageSearch,
  loyalty: Medal,
  wishlist: Heart,
  addresses: MapPin,
  settings: Settings,
  logout: LogOut,
}

export default function ProfileDrawer({ open, onClose, vendor, customer, isLoading }: { open: boolean, onClose: () => void, vendor: any, customer: any, isLoading: boolean, setIsLoading: (loading: boolean) => void }) {
  const { accountItems, logoutItem } = useProfileNav()
  const visibleItems = accountItems.filter(item => item.key !== 'loyalty' || isLoyaltyActive(vendor))
  const displayName = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim() || customer?.email || 'Хэрэглэгч'

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50 font-inter" onClose={onClose}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>
        <div className="fixed inset-0 z-50 flex justify-end">
          <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="translate-x-full">
            <DialogPanel className="relative flex w-[82%] max-w-xs flex-col bg-cream shadow-2xl rounded-l-3xl overflow-hidden">
              <div className="h-14 px-5 flex items-center justify-between border-b border-foreground/5 shrink-0">
                <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-foreground/55">Профайл</span>
                <button onClick={onClose} aria-label="Хаах" className="w-9 h-9 inline-flex items-center justify-center rounded-full text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors">
                  <X className="size-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain">
                <div className="px-5 pt-5 pb-4">
                  {isLoading ? (
                    <div className="flex items-center gap-3">
                      <Skeleton className="size-14 rounded-2xl" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="w-32 h-4" />
                        <Skeleton className="w-40 h-3" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                        <div className="size-14 rounded-2xl overflow-hidden bg-foreground/5">
                          {customer?.avatar ? (
                            <img src={formatImageURL(customer.avatar)} alt="" className="size-14 object-cover" />
                          ) : (
                            <Avatar size={56} variant="marble" name={customer?.email || customer?.firstName || vendor?.name || 'shop'} />
                          )}
                        </div>
                        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-cream" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[15px] font-bold font-gilroy text-foreground truncate tracking-[-0.01em]">{displayName}</div>
                        {customer?.email && (
                          <div className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-foreground/60">
                            <ShieldCheck className="size-3 text-emerald-600" />
                            <span className="truncate">{customer.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <ul className="px-2 pb-3">
                  {visibleItems.map(item => {
                    const Icon = ICONS[item.key]
                    return (
                      <li key={item.key}>
                        <Link
                          href={item.url}
                          onClick={onClose}
                          className={`flex items-center gap-3 rounded-xl px-3 h-12 text-[15px] font-medium transition-colors ${item.isActive ? 'bg-foreground/5 text-foreground' : 'text-foreground hover:bg-foreground/5'}`}
                        >
                          <span className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-foreground/5 text-foreground/70">
                            <Icon className="size-4" />
                          </span>
                          <span className="flex-1">{item.name}</span>
                          <ChevronRight className="size-4 opacity-40" />
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>

              {logoutItem && (
                <div className="border-t border-foreground/5 p-4 shrink-0">
                  <Link
                    href={logoutItem.url}
                    onClick={onClose}
                    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-cream text-[15px] font-semibold hover:bg-foreground/90 transition-colors"
                  >
                    <LogOut className="size-4" />{logoutItem.name}
                  </Link>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
